import React, { useState, useEffect, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { ArrowLeft } from 'lucide-react';
import { View, VocabWord } from './src/types';
import { GAME_CONFIG, THEMES } from './src/config/constants';
import { useGameState } from './src/hooks/useGameState';
import { useTextToSpeech } from './src/hooks/useTextToSpeech';
import { apiClient } from './src/utils/api';
import { getRandomItem } from './src/utils/helpers';

// Components
import HomeMenu from './src/components/HomeMenu';
import VocabGame from './src/components/VocabGame';
import ChatTutor from './src/components/ChatTutor';
import LoadingScreen from './src/components/LoadingScreen';

// Main App Component
const App = () => {
  const [currentView, setCurrentView] = useState<View>('home');
  const [preloadedVocab, setPreloadedVocab] = useState<VocabWord[] | null>(null);
  const [isPreloading, setIsPreloading] = useState(false);

  // Custom hooks
  const { gameState, saveGameState } = useGameState();
  const { speak } = useTextToSpeech();

  // Preload vocabulary on app startup - only once
  useEffect(() => {
    const preloadVocab = async () => {
      setIsPreloading(true);
      try {
        const theme = getRandomItem(THEMES).name;
        const words = await apiClient.getVocabulary(theme);
        setPreloadedVocab(words);
      } catch (error) {
        console.log('Preload failed, will load on demand:', error);
      } finally {
        setIsPreloading(false);
      }
    };

    preloadVocab();
  }, []); // 空依赖，只在 mount 时执行一次

  // Handle navigation back to home
  const handleBackToHome = useCallback(() => {
    setCurrentView('home');
  }, []);

  // Handle view selection
  const handleViewSelect = useCallback((view: View) => {
    setCurrentView(view);
  }, []);

  // Handle preload complete
  const handlePreloadComplete = useCallback(() => {
    setPreloadedVocab(null);
  }, []);

  // Render main content
  const renderMainContent = () => {
    switch (currentView) {
      case 'home':
        return (
          <HomeMenu
            onSelect={handleViewSelect}
            gameState={gameState}
            hasPreloadedVocab={!!preloadedVocab}
          />
        );

      case 'vocab':
        return (
          <VocabGame
            gameState={gameState}
            saveGameState={saveGameState}
            preloadedVocab={preloadedVocab}
            onPreloadComplete={handlePreloadComplete}
          />
        );

      case 'chat':
        return <ChatTutor gameState={gameState} saveGameState={saveGameState} />;

      default:
        return (
          <HomeMenu
            onSelect={handleViewSelect}
            gameState={gameState}
            hasPreloadedVocab={!!preloadedVocab}
          />
        );
    }
  };

  return (
    <div className="min-h-screen h-screen flex flex-col bg-gradient-to-br from-purple-400 via-pink-300 to-sky-400 animate-gradient-shift overflow-hidden">
      <div className="w-full h-full bg-white/95 backdrop-blur-sm flex flex-col relative">
        {/* Header with Game Stats */}
        <div className="bg-gradient-to-r from-sky-400 to-purple-500 p-3 flex items-center justify-between text-white shadow-lg z-10 flex-shrink-0">
          {currentView !== 'home' && (
            <button
              onClick={handleBackToHome}
              className="hover:bg-white/20 p-2 rounded-full transition-all hover:scale-110"
              title="Back to home"
            >
              <ArrowLeft size={28} />
            </button>
          )}

          <h1 className="text-2xl font-bold flex-1 text-center tracking-wider text-shadow animate-pulse flex items-center justify-center">
            🌟 English Adventure 🌟
            {isPreloading && (
              <div className="ml-3 flex items-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span className="ml-1 text-xs">Loading...</span>
              </div>
            )}
          </h1>

          {currentView !== 'home' && (
            <div className="w-[40px]">{/* Spacer for balance */}</div>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto relative bg-gradient-to-b from-sky-50 to-purple-50">
          {renderMainContent()}
        </div>
      </div>
    </div>
  );
};

// Initialize and render app
const root = createRoot(document.getElementById('root')!);
root.render(<App />);